/**
 * The domain model, with no I/O and no dependency on how any of it is stored.
 *
 * Persisted shapes use ISO strings rather than `Date`, because these objects go
 * into DynamoDB and come back out of JSON. A `Date` that survives a write and
 * returns as a string is the kind of type lie that typechecks all the way to
 * production.
 */
/**
 * The name of a language, for telling a model which one to write in.
 *
 * **A locale code is not an instruction.** Every generation prompt used to say "write
 * in the same language as the objective", which asks the model to infer the language
 * from data it has been told to treat as untrusted — and inference is exactly what
 * drifts. A curriculum for an English objective, built from documents whose own front
 * matter declared `language="en"`, came back entirely in German. Nothing was
 * misconfigured; the instruction was simply relative where it needed to be absolute.
 *
 * So prompts name the language. This is the only place that mapping lives, because
 * three pipelines need the same answer and three copies of it would drift apart.
 */
export const LANGUAGE_NAMES = {
    en: 'English',
    es: 'Spanish (Latin American)',
};
export function languageName(locale) {
    return LANGUAGE_NAMES[locale];
}
/**
 * A smoke test for "did the model write in the language it was told to".
 *
 * Not language detection, and not trying to be: it looks for a handful of words that
 * are near-unavoidable in running prose in the expected language, and complains only
 * when *none* of them appear across a whole curriculum's worth of text. That is a
 * deliberately low bar — it cannot tell good Spanish from bad, and it will not fire on
 * a few borrowed terms, which is right, because technical material is full of them.
 *
 * It exists because the failure it catches is expensive and quiet. A learner
 * memorises a curriculum; one silently generated in the wrong language wastes the
 * research it was built from and every item generated afterwards. Failing the pass
 * costs one cheap re-run.
 */
const LANGUAGE_MARKERS = {
    en: ['the', 'and', 'of', 'for', 'with', 'that', 'from', 'which'],
    es: ['el', 'la', 'los', 'las', 'de', 'que', 'para', 'con', 'del', 'y'],
};
export function looksLikeLanguage(text, locale) {
    // Short text carries no signal — a handful of titles legitimately contains no
    // function words at all, and failing on that would be worse than not checking.
    const words = text.toLowerCase().match(/[\p{L}]+/gu) ?? [];
    if (words.length < 40)
        return true;
    const markers = new Set(LANGUAGE_MARKERS[locale]);
    return words.some((word) => markers.has(word));
}
/**
 * Whether a node is in play, following its parent when it has one.
 *
 * Selection lives only on top-level nodes, so a nested topic's own `active` is
 * meaningless and is deliberately not consulted: the question "is this being
 * studied" has exactly one answer per subject, and storing it per leaf would let a
 * subtree disagree with the section heading the learner unticked.
 */
export function isActiveNode(node, byId) {
    // Bounded rather than recursive: a `parentId` cycle is a corrupt curriculum, and
    // the honest response is to treat the node as active and let it be seen, not to
    // hang the pass that reads it.
    let current = node;
    for (let depth = 0; depth < 32; depth++) {
        if (!current.parentId)
            return current.active !== false;
        const parent = byId.get(current.parentId);
        // An orphan is treated as its own root, matching how the outline renders it —
        // it is visible to the learner, so it must be answerable here too.
        if (!parent)
            return current.active !== false;
        current = parent;
    }
    return true;
}
//# sourceMappingURL=domain.js.map